<script lang="ts" setup>
	import useAccountApi from '~/services/apis/account'
	import useEmployeesApi from '~/services/apis/employeesApi'

	const props = defineProps<{
		show: boolean
		staffName: string
		staffId: string
		planName?: string
	}>()
	const emits = defineEmits<{
		(event: 'done'): void
		(event: 'update:show', param: boolean): void
	}>()
	const { confirmPasswordApi } = useAccountApi()
	const { removeEmployee } = useEmployeesApi()
	const removalForm = reactive({
		terms: false,
		password: ''
	})
	const modalNeedToKnow = computed(() => {
		return [
			`You are about to remove this staff <span class='text-gray-600 font-bold capitalize'>${props.staffName}</span>`,
			'Note that the staff will be removed from your business dashboard.',
			'This action will also remove all policies and dependants attached to this staff.'
		]
	})
	const submittingRecords = shallowRef(false)
	const isTermsDialog = shallowRef(false)
	const terminateStaffRecord = async () => {
		try {
			submittingRecords.value = true
			const confirmedResult = await confirmPasswordApi({
				password: removalForm.password
			})
			if (confirmedResult?.data) {
				submittingRecords.value = true
				const result = await removeEmployee('manual', { employee_ids: [props.staffId], instant_removal: true })
				if (result.value?.data) {
					resolve(() =>
						useMixpanel().track('terminate_employee', {
							business_name: useUser().User?.customer?.name ?? '',
							staff: props.staffName
						})
					)
					useNotification().alert({
						message: `${props.staffName} has been removed from your business dashbard.`,
						status: 200
					})
					emits('update:show', false)
					emits('done')
					refreshNuxtData(['employee-details', 'policies'])
				}
			}
		} finally {
			submittingRecords.value = false
		}
	}
</script>

<template>
	<dialogs-error
		:show="show"
		type="warning"
		header="Remove Staff"
		wrapper-class="bg-gray-50 px-4 py-8 rounded-md w-full space-y-5"
		@update:show="$emit('update:show', false)"
	>
		<template #paragraph>
			<div class="text-left">
				<span class="text-sm text-blue-950">Need to know</span>

				<content-list-item-disc :list="modalNeedToKnow" />
			</div>
		</template>

		<template #p>
			<a-form :model="removalForm" :validate-messages="antValidateMessages" @finish="terminateStaffRecord">
				<a-form-item v-show="removalForm.terms" label="Password" required name="password" class="!mt-12">
					<a-input-password
						v-model:value="removalForm.password"
						placeholder="Enter your password"
						autocomplete="new password"
						class="custom-input w-full !bg-gray-50 !px-5"
					/>
				</a-form-item>

				<a-form-item v-show="!removalForm.terms" name="terms" required>
					<div class="mt-10 flex w-full items-center justify-center gap-x-2">
						<a-checkbox v-model:checked="removalForm.terms" />
						<span class="text-gray-600">
							I have read and I accept the
							<span
								role="button"
								aria-label="button"
								class="font-medium text-primary-2"
								@click="() => (isTermsDialog = true)"
							>
								Terms & Conditions
							</span>
						</span>
					</div>
				</a-form-item>

				<div class="flex w-full flex-wrap items-center justify-between gap-4 sm:mt-12 sm:gap-10">
					<button
						type="button"
						name="back"
						class="default-btn flex-1 justify-center bg-gray-200"
						@click="() => (removalForm.terms = false)"
					>
						Back
					</button>

					<a-button
						:disabled="!removalForm.password || !removalForm.terms"
						:loading="submittingRecords"
						name="remove"
						html-type="submit"
						class="default-btn flex-1 justify-center border-error-500 !bg-error-500 !text-white disabled:!bg-error-200"
					>
						Remove
					</a-button>
				</div>
			</a-form>
			<dialogs-terms-and-condition v-model:show="isTermsDialog" />
		</template>

		<template #button>
			<span />
		</template>
	</dialogs-error>
</template>
